import {Card, CardBody, CardHeader, SelectItem} from "@heroui/react";
import {TableBody, TableCell, TableColumn, TableHeader, TableRow} from "@heroui/table";
import {addToast} from "@heroui/toast";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute} from '@tanstack/react-router'
import {useWindowSize} from "@uidotdev/usehooks";
import React, {useState} from "react";
import {apiActionImproved} from "../../../api.ts";
import Content from "../../../components/Content.tsx";
import PButton from "../../../components/PButton.tsx";
import PInput from "../../../components/PInput.tsx";
import PSelect from "../../../components/PSelect.tsx";
import PTable from "../../../components/PTable.tsx";
import Header from "../../../Header.tsx";
import services from "../../../services";
import TournamentsRankingTablesList from "../../../tournaments/RankingTables/List.tsx";
import {RankingGroup} from "../../../types";
import {tableFullScreen} from "../../../user/courts/Edit.tsx";
import {maybeUndefinedDefaultKey} from "../../../utilities.ts";
import queryKeys from "../../../utilities/queryKeys.ts";

export const Route = createFileRoute('/_app/running/upload')({
    component: RouteComponent,
})

function RouteComponent() {
    const queryClient = useQueryClient();

    const [file, setFile] = useState<File | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFile(e.target.files[0]);
        }
    };

    const importsQuery = useQuery({
        queryKey: queryKeys.running.getImports(),
        queryFn:  async () => services.running.getImports(),
    })

    const rankingGroupsQuery = useQuery({
        queryKey: queryKeys.running.getRankings(),
        queryFn:  async () => services.running.getRankings(),
    });

    const handleUpload = async () => {
        if (file) {
            console.log('Uploading file...');

            const formData = new FormData();
            formData.append('file', file);

            try {
                // You can write the URL of your server or any other endpoint used for file upload
                const result = await apiActionImproved('running-upload' as any, {}, {
                    body: formData,
                });

                console.log(result);
                setFile(null);

                addToast({
                    title: "Import successful",
                    color: "success",
                })
                queryClient.invalidateQueries({queryKey: queryKeys.running.getImports()})
            } catch (error: any) {
                console.log('error', error)
                if (error.message) {
                    addToast({
                        title: error.message,
                        color: "danger",
                    })
                } else {
                    alert("Error: " + error);
                }
            }
        }
    };

    const deleteMut = useMutation({
        mutationFn: services.running.deleteImport,
        onSuccess:  () => {
            queryClient.invalidateQueries({queryKey: queryKeys.running.getImports()})

            addToast({
                title: 'Removed',
                color: 'success',
            });
        },
        onError(error) {
            addToast({color: "danger", title: error?.message || "Something went wrong."});
        }
    })

    const updateMut = useMutation({
        mutationFn: services.running.updateImport,
        onSuccess:  () => {
            queryClient.invalidateQueries({queryKey: queryKeys.running.getImports()})

            addToast({
                title: 'Removed',
                color: 'success',
            });
        },
        onError(error) {
            addToast({color: "danger", title: error?.message || "Something went wrong."});
        }
    })

    const size = useWindowSize();

    const columns = (size.width && size.width < 1024
            ? [
                {key: 'combined', title: 'Import'},
                {key: 'actions', title: ''},
            ]
            : [
                {key: 'file_name', title: 'Import File'},
                {key: 'leaderboard', title: 'Leaderboard'},
                {key: 'created_at', title: 'Date'},
                {key: 'actions', title: ''},
            ]
    );

    async function startImportRename(imp) {
        const name = prompt('Enter race name', imp.event_title || imp.file_name);
        if (name !== null) {
            await updateImport(imp, {event_title: name});
            queryClient.invalidateQueries({queryKey: queryKeys.running.getImports()})
        }
    }

    async function changeImportLeaderboard(imp, leaderboard_uid: string) {
        await updateImport(imp, {ranking_group_uid: leaderboard_uid});
        queryClient.invalidateQueries({queryKey: queryKeys.running.getImports()})
    }

    async function updateImport(imp, fields) {
        await services.running.updateImport({
            uid:     imp.uid,
            version: imp.version + 1,
            ...fields,
        });
        queryClient.invalidateQueries({queryKey: queryKeys.running.getImports()})
    }

    return <>
        <Header backUrl={"/running"} />
        <Content>
            <Card className={"mb-4"}>
                <CardBody>
                    <TournamentsRankingTablesList title={<div className={"font-bold"}>Leaderboards</div>} sport_type={"running"} />
                </CardBody>
            </Card>

            <Card>
                <CardHeader className={"flex items-center justify-between"}>
                    <div className={"font-bold text-lg"}>Races</div>
                </CardHeader>
                <CardBody>

                    <Card className={"mb-4"}>
                        <CardHeader className={"flex items-center justify-between font-bold"}>
                            Import new results
                        </CardHeader>
                        <CardBody>
                            <div>
                                <div className={"mb-4"}>
                                    <PInput type={"file"} onChange={handleFileChange} />
                                </div>
                                {file && (
                                    <Card className={"mb-4"}>
                                        <CardBody>
                                            <ul>
                                                <li>Type: {file.type}</li>
                                                <li>Size: {file.size} bytes</li>
                                            </ul>
                                        </CardBody>
                                    </Card>
                                )}
                                {file && (
                                    <PButton
                                        color={"primary"}
                                        onPress={handleUpload}
                                    >Import</PButton>
                                )}
                            </div>
                        </CardBody>
                    </Card>


                    <PTable removeWrapper={true} classNames={tableFullScreen} isStriped={true} aria-label={"Running Results"}>
                        <TableHeader columns={columns}>
                            {(column) => <TableColumn key={column.key}>{column.title}</TableColumn>}
                        </TableHeader>
                        <TableBody>
                            {(importsQuery.data || []).map((result: any) => <TableRow key={result.uid}>
                                {(columnKey) => {
                                    if (columnKey === 'combined') {
                                        return <TableCell>
                                            Race: <strong>{result.event_title}</strong><br />
                                            <small>Import file: {result.file_name}</small>
                                            <br />
                                            <small>{result.created_at}</small>
                                        </TableCell>
                                    }
                                    if (columnKey === 'file_name') {
                                        return <TableCell>
                                            Race: <strong>{result.event_title || result.file_name}</strong>
                                            <PButton className={"ml-2"} size={"sm"} onPress={() => startImportRename(result)}>Rename</PButton>
                                            <br />
                                            <small>Import file: {result.file_name}</small>
                                            <br />
                                        </TableCell>
                                    }
                                    if (columnKey === 'leaderboard') {
                                        return <TableCell>
                                            <PSelect
                                                selectedKeys={maybeUndefinedDefaultKey(result.ranking_group_uid)}
                                                onSelectionChange={(e) => {
                                                    changeImportLeaderboard(result, e.currentKey as any)
                                                }}
                                                label={""}
                                                disallowEmptySelection={true}
                                            >
                                                {(rankingGroupsQuery.data || []).map((rankingGroup: RankingGroup) => (
                                                    <SelectItem key={rankingGroup.uid}>{rankingGroup.name}</SelectItem>
                                                ))}
                                            </PSelect>
                                        </TableCell>
                                    }
                                    if (columnKey === 'actions') {
                                        return <TableCell>
                                            <PButton color={"danger"} onPress={() => deleteMut.mutate(result.uid)}>
                                                Delete
                                            </PButton>
                                        </TableCell>
                                    }
                                    if (result.hasOwnProperty(columnKey)) {
                                        return <TableCell>
                                            {result[columnKey] || '-'}
                                        </TableCell>
                                    }
                                    return (
                                        <TableCell>
                                            {columnKey}
                                        </TableCell>
                                    );
                                }}
                            </TableRow>)}
                        </TableBody>

                    </PTable>
                </CardBody>
            </Card>
        </Content>
    </>
}
